import type { Timesheet } from '@clockwise/clients/timesheets';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { timesheetsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { ApprovalResolutionNote, attentionBannerClass } from './approval-resolution-note';
import { type ApprovalResolution, approvalResolution } from './approval-resolution-text';
import { hoursValidationError, longDayName, shortDate } from './timesheet-week';

export interface CellEditorValue {
  /** Null is allowed: drafts can be incomplete and saving with empty hours
   *  patches `hours` to null on the backend instead of silently closing. */
  hours: number | null;
  description: string | null;
  billable: boolean;
}

interface Props {
  date: string;
  entry: Timesheet | null;
  /** Used as the initial billable when creating from blank. */
  defaultBillable?: boolean;
  /** Internal customers can't be invoiced, so their time is never billable.
   *  When true the billable toggle is forced off and disabled, and the
   *  saved value is always false. */
  customerIsInternal?: boolean;
  /** Pre-fills the description on a *new* entry (entry === null). Ignored
   *  when editing an existing entry — a user who explicitly saved with an
   *  empty description shouldn't have it re-populated on reopen. */
  defaultDescription?: string | null;
  /** Submit-time field error for this entry (missing hours OR description).
   *  Rendered inline so opening a flagged cell points at what to fix. */
  submitError?: string;
  /** Sum of the day's OTHER entries (excluding this one), used to enforce the
   *  24h/day cap inline. Defaults to 0 — a lone entry never trips the cap. */
  otherHoursOnDay?: number;
  saving?: boolean;
  onSave: (value: CellEditorValue) => void;
  /** Delete the existing entry. No-op when `entry` is null. */
  onClear: () => void;
  onClose: () => void;
  /** Reports whether the draft differs from the seeded baseline, so the caller
   *  can guard an accidental dismissal (outside-click) against losing edits. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Body of the per-cell popover. Owns draft state and validation; the caller
 * decides what the result means (CREATE, PATCH, DELETE) based on whether
 * `entry` was passed. Renders no chrome — wrap in a Popover or Sheet.
 */
export function TimesheetCellEditor({
  date,
  entry,
  defaultBillable = false,
  customerIsInternal = false,
  defaultDescription,
  submitError,
  otherHoursOnDay = 0,
  saving,
  onSave,
  onClear,
  onClose,
  onDirtyChange,
}: Props) {
  // Seeded baseline for each field; reused both as the initial draft and as the
  // reference the dirty check compares against to detect unsaved edits.
  const initialHours = entry?.hours != null ? String(entry.hours) : '';
  const initialDescription = entry ? (entry.description ?? '') : (defaultDescription ?? '');
  const initialBillable = entry?.billable ?? defaultBillable;
  const [hoursDraft, setHoursDraft] = useState(initialHours);
  const [description, setDescription] = useState(initialDescription);
  const [billable, setBillable] = useState(initialBillable);
  // Set when the user tries to save a cell that has hours but no
  // description — the same condition the server enforces at submit, pulled
  // forward so the save itself is blocked instead of only the later submit.
  const [descriptionError, setDescriptionError] = useState(false);
  // Inline hours error message (malformed / out-of-range value, or a value
  // that would push the day past 24h), or null when the field is acceptable.
  // Keeps the typed text and shows the message instead of silently closing.
  const [hoursError, setHoursError] = useState<string | null>(null);
  // Internal customers force billable off; the toggle is disabled and the
  // effective value drives both the UI and the saved payload.
  const effectiveBillable = customerIsInternal ? false : billable;
  const hoursRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Who/when the entry was rejected, fetched lazily from the approval system
  // only when a rejected/failed cell is open — nothing is denormalized onto
  // the row. The outcome is terminal, so cache it indefinitely.
  const entryId = entry?.id;
  const isRejected = entry?.status === 'rejected' || entry?.status === 'failed';
  const rejectionQuery = useQuery({
    queryKey: queryKeys.timesheets.rejection(entryId ?? ''),
    // entryId is always defined when enabled; the `?? ''` only satisfies the type.
    queryFn: () => timesheetsApi.getRejection(entryId ?? ''),
    enabled: Boolean(entryId) && isRejected,
    staleTime: Number.POSITIVE_INFINITY,
  });
  // "<date> · <who>" suffix on the banner — each part shown only when known
  // (system-driven outcomes resolve a date but no rejecter name).
  const rejection = rejectionQuery.data?.data;
  const rejectionMeta = [
    formatRejectionDate(rejection?.resolvedAt),
    rejection?.rejectedByName ?? null,
  ]
    .filter(Boolean)
    .join(' · ');
  // The reason text plus who wrote it, so the banner can quote an approver's
  // own words and leave the app's copy plain. The entry's status settles it
  // (see `approvalResolution`) for everything written since the no-match
  // mapping shipped.
  //
  // The lookup above then corrects the one case status can't: a pre-mapping
  // no-match row, still sitting at `rejected` with server prose. A resolved
  // instance with no `rejectedByName` had no human decider, so whatever the
  // status says, nobody typed that reason. This view can afford the extra
  // fetch; the read-only locked cell can't, which is why the backfill is
  // still the real fix for those rows.
  const resolutionFromEntry = approvalResolution(entry?.resolutionReason, entry?.status ?? '');
  const resolution: ApprovalResolution | null =
    resolutionFromEntry && rejectionQuery.isSuccess && !rejection?.rejectedByName
      ? { kind: 'system', text: resolutionFromEntry.text }
      : resolutionFromEntry;

  useEffect(() => {
    hoursRef.current?.focus();
    hoursRef.current?.select();
  }, []);

  // Surface "has unsaved edits" to the caller so an accidental outside-click can
  // prompt before discarding. Compares the live draft to the seeded baseline.
  const isDirty =
    hoursDraft !== initialHours ||
    description !== initialDescription ||
    billable !== initialBillable;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const commit = () => {
    const trimmed = hoursDraft.trim();
    // Validate before saving. Bad input (non-numeric, ≤ 0, > 24, or a value
    // that would push the day past the 24h cap) blocks the save and flags the
    // field inline so the user sees what's wrong and can fix it — instead of
    // silently closing (or rewriting what they typed) and losing their input.
    const error = hoursValidationError(trimmed, otherHoursOnDay);
    if (error) {
      setHoursError(error);
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    // Empty is allowed: drafts can save with null hours (PATCHes hours → null;
    // callers route to DELETE separately via onClear).
    const nextHours = trimmed === '' ? null : Number(trimmed);
    // A cell with hours must carry a description. Block the save (rather than
    // letting it through to be rejected at submit) and point at the field.
    // Cells with no hours are drafts the user may be clearing, so they save
    // freely — mirrors the server's submit-time rule.
    if (nextHours != null && description.trim() === '') {
      setDescriptionError(true);
      descriptionRef.current?.focus();
      return;
    }
    const previousHours = entry?.hours != null ? Number(entry.hours) : null;
    if (
      entry &&
      previousHours === nextHours &&
      (entry.description ?? '') === description.trim() &&
      entry.billable === effectiveBillable
    ) {
      onClose();
      return;
    }
    onSave({
      hours: nextHours,
      description: description.trim() || null,
      billable: effectiveBillable,
    });
  };

  return (
    <div className="space-y-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {longDayName(date).slice(0, 3)}, {shortDate(date)}
      </div>

      {submitError ? (
        <div className="flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
          <AlertCircle className="size-3.5 shrink-0" />
          {submitError}
        </div>
      ) : null}

      {entry && (entry.status === 'rejected' || entry.status === 'failed') ? (
        <div
          className={cn(
            'rounded-md border px-2.5 py-2 text-xs',
            attentionBannerClass(entry.status),
          )}
        >
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 font-medium">
            <AlertCircle className="size-3.5 shrink-0" />
            <span>{entry.status === 'rejected' ? 'Rejected' : 'Needs review'}</span>
            {rejectionQuery.isLoading ? (
              <Loader2
                className="size-3 shrink-0 animate-spin opacity-70"
                aria-label="Loading details"
              />
            ) : rejectionMeta ? (
              <span className="font-normal opacity-80">· {rejectionMeta}</span>
            ) : null}
          </div>
          {resolution ? <ApprovalResolutionNote resolution={resolution} /> : null}
          <p className="mt-1.5 text-[11px] opacity-80">
            {entry.status === 'rejected'
              ? 'Update the timesheet entry, then resubmit for approval.'
              : // `failed` covers no approver AND no matching policy (both land
                // here) — naming only the approver case was wrong for half of
                // them. Either way the fix is an admin's, and the entry is
                // resubmitted automatically once they make it.
                "This couldn't be sent for approval — contact your admin."}
          </p>
        </div>
      ) : null}

      <div>
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Hours
        </div>
        <Input
          ref={hoursRef}
          type="text"
          inputMode="decimal"
          aria-label="Hours"
          value={hoursDraft}
          placeholder="0"
          disabled={saving}
          aria-invalid={hoursError ? true : undefined}
          onChange={(e) => {
            // Keep the raw text — don't rewrite what the user typed. Invalid
            // input surfaces as an inline error on save, not silently stripped.
            // The error clears as soon as they start fixing it.
            setHoursDraft(e.target.value);
            if (hoursError) setHoursError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          className="mt-1 tabular-nums"
        />
        {hoursError ? <p className="mt-1 text-[11px] text-destructive">{hoursError}</p> : null}
      </div>

      <div>
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Description <span className="text-destructive">*</span>
        </div>
        <Textarea
          ref={descriptionRef}
          rows={2}
          aria-label="Description"
          value={description}
          disabled={saving}
          onChange={(e) => {
            setDescription(e.target.value);
            if (descriptionError) setDescriptionError(false);
          }}
          placeholder="What did you work on?"
          aria-invalid={descriptionError || undefined}
          // shadcn Textarea uses `field-sizing-content` (auto-grow to fit
          // content). max-h caps the growth at ~10 rows so a long, pre-
          // filled product description doesn't push the popover off-
          // screen; overflow-y-auto reveals the rest inline.
          className="mt-1 max-h-52 resize-none overflow-y-auto"
        />
        {descriptionError ? (
          <p className="mt-1 text-[11px] text-destructive">Add a description before saving.</p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => setBillable((b) => !b)}
        disabled={saving || customerIsInternal}
        className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/50 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
      >
        <div>
          <div className="text-sm font-medium">Billable</div>
          <div className="text-[11px] text-muted-foreground">
            {customerIsInternal
              ? 'Internal customer — never billable'
              : effectiveBillable
                ? 'Included in customer invoice'
                : 'Internal / not invoiced'}
          </div>
        </div>
        <span
          className={cn(
            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
            effectiveBillable ? 'bg-primary' : 'bg-muted',
          )}
          aria-hidden
        >
          <span
            className={cn(
              'inline-block size-4 rounded-full bg-background shadow transition-transform',
              effectiveBillable ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </span>
      </button>

      {/* Delete is left-aligned and destructive so it reads apart from the
          Cancel/Save pair; shown only for an existing entry (a new cell has
          nothing to delete). Routes through `onClear`, the same handler the
          old top-right "Clear" used.
          Sticky to the bottom of the popover's scroll box so Save never
          scrolls out of reach on a tall editor. `-bottom-3`/`-mx-3`/`-mb-3`
          bleed over the popover's `p-3` (timesheet-week-grid.tsx) so the bar
          sits flush with its edge and nothing peeks out beneath it. */}
      <div
        className={cn(
          'sticky -bottom-3 -mx-3 -mb-3 flex items-center gap-2 bg-popover px-3 pt-1 pb-3',
          entry ? 'justify-between' : 'justify-end',
        )}
      >
        {entry ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-tone="destructive"
            onClick={onClear}
            disabled={saving}
            className="text-destructive"
          >
            <Trash2 className="size-3.5" /> Delete
          </Button>
        ) : null}
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={commit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Format the rejection timestamp (an ISO string) as a short local date, e.g.
 *  "Jun 8, 2026". Returns null for missing/unparseable input so the caller
 *  can omit the segment. */
function formatRejectionDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
